import { type Cosmetic } from '@prisma/client'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { useLoaderData, Link } from '@remix-run/react'
import { motion, useAnimation } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { DynamicShowcase } from '#app/components/marketing/dynamic-showcase'
import { FeaturedCosmetics } from '#app/components/marketing/featured-skins'
import { HeroSection } from '#app/components/marketing/hero-section'
import { Navigation } from '#app/components/navigation'
import { prisma } from '#app/utils/db.server.ts'
import { slugify } from '#app/utils/misc.tsx'

type LoaderData = {
	featuredCosmetics: (Omit<
		Cosmetic,
		| 'added'
		| 'updatedAt'
		| 'createdAt'
		| 'shopHistory'
		| 'images'
		| 'gameplayTags'
		| 'metaTags'
	> & {
		images: Record<string, string>
		added: string
		updatedAt: string
		createdAt: string
		shopHistory: string[] | null
		gameplayTags: string[]
		metaTags: string[]
		slug: string // Added slug property
	})[]
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const featuredCosmetics = await prisma.cosmetic.findMany({
		where: {
			type: 'outfit',
			images: {
				not: '',
			},
		},
		take: 15,
	})

	return json({
		featuredCosmetics: featuredCosmetics.map(cosmetic => ({
			...cosmetic,
			images: JSON.parse(cosmetic.images as string),
			added: cosmetic.added.toISOString(),
			updatedAt: cosmetic.updatedAt.toISOString(),
			createdAt: cosmetic.createdAt.toISOString(),
			shopHistory: cosmetic.shopHistory
				? JSON.parse(cosmetic.shopHistory)
				: null,
			gameplayTags: JSON.parse(cosmetic.gameplayTags),
			metaTags: JSON.parse(cosmetic.metaTags),
			slug: slugify(cosmetic.name), // Add this line to create a slug for each cosmetic
		})),
	})
}

export default function Index() {
	const { featuredCosmetics } = useLoaderData<LoaderData>()
	const [showNav, setShowNav] = useState(false)

	useEffect(() => {
		const handleScroll = () => {
			setShowNav(window.scrollY > 100)
		}

		window.addEventListener('scroll', handleScroll)
		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	return (
		<div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white">
			{showNav && (
				<motion.div
					initial={{ opacity: 0, y: -50 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3 }}
					className="fixed left-0 right-0 top-0 z-50"
				>
					<Navigation isHomePage={true} />
				</motion.div>
			)}
			<HeroSection />
			<AnimatedSection>
				{featuredCosmetics.length > 0 ? (
					<DynamicShowcase
						cosmetics={featuredCosmetics.map(cosmetic => ({
							...cosmetic,
							link: `/cosmetics/${cosmetic.slug}`, // Update the link to use the slug
						}))}
					/>
				) : (
					<div>No featured cosmetics available</div>
				)}
			</AnimatedSection>
			<AnimatedSection>
				<FeaturedCosmetics
					cosmetics={featuredCosmetics.map(cosmetic => ({
						...cosmetic,
						link: `/cosmetics/${cosmetic.slug}`, // Update the link to use the slug
					}))}
				/>
			</AnimatedSection>
			{/* <AnimatedSection>
				<StatsSection totalSkins={totalSkins} />
			</AnimatedSection>
			<AnimatedSection>
				<NewsSection />
			</AnimatedSection>
			<AnimatedSection>
				<InteractiveFooter />
			</AnimatedSection> */}
		</div>
	)
}

function AnimatedSection({ children }: { children: React.ReactNode }) {
	const controls = useAnimation()
	const [ref, inView] = useInView({
		triggerOnce: true,
		threshold: 0.1,
	})

	useEffect(() => {
		if (inView) {
			controls.start('visible')
		}
	}, [controls, inView])

	return (
		<motion.div
			ref={ref}
			animate={controls}
			initial="hidden"
			variants={{
				visible: { opacity: 1, y: 0 },
				hidden: { opacity: 0, y: 50 },
			}}
			transition={{ duration: 0.5 }}
		>
			{children}
		</motion.div>
	)
}
